/**
 * Component Showcase Page
 *
 * TEMPORARY DEVELOPMENT ROUTE - FOR MIGRATION REFERENCE ONLY
 *
 * This page showcases all shadcn/ui components created during the MUI to Tailwind migration (Epic 1).
 * It provides visual examples and code references for developers during the migration process.
 *
 * TO BE REMOVED OR HIDDEN AFTER MIGRATION COMPLETION (Epic 6)
 *
 * Environment variable control: VITE_SHOW_COMPONENT_SHOWCASE
 * - Set to "true" in development to show this route
 * - Set to "false" in production to hide this route
 */

import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Calendar } from '@/components/ui/calendar'
import { DatePicker } from '@/components/ui/date-picker'
import { Container } from '@/components/ui/container'
import { Stack } from '@/components/ui/stack'
import { Text } from '@/components/ui/text'
import { LoadingButton } from '@/components/ui/loading-button'
import {
  XCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  Code,
  Copy,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_auth/component-showcase')({
  component: ComponentShowcase,
})

/**
 * CodeBlock component for displaying code examples with syntax highlighting
 */
interface CodeBlockProps {
  code: string
  className?: string
}

function CodeBlock({ code, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={cn('relative group', className)}>
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 p-2 rounded-md bg-muted hover:bg-muted/80 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        aria-label="Copy code to clipboard"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
      <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
        <code className="text-foreground font-mono">{code}</code>
      </pre>
    </div>
  )
}

/**
 * Section component for organizing showcase content
 */
interface SectionProps {
  id: string
  title: string
  description?: string
  children: React.ReactNode
}

function Section({ id, title, description, children }: SectionProps) {
  return (
    <section id={id} className="space-y-4 scroll-mt-20">
      <div className="space-y-2">
        <Text variant="h2" className="flex items-center gap-2">
          <Code className="h-6 w-6" aria-hidden="true" />
          {title}
        </Text>
        {description && (
          <Text variant="body2" className="text-muted-foreground">
            {description}
          </Text>
        )}
      </div>
      <div className="space-y-6">{children}</div>
    </section>
  )
}

/**
 * Example component for displaying component examples
 */
interface ExampleProps {
  title: string
  children: React.ReactNode
}

function Example({ title, children }: ExampleProps) {
  return (
    <div className="space-y-3">
      <Text variant="h4" className="text-base font-medium">
        {title}
      </Text>
      <div className="border rounded-lg p-6 bg-background">{children}</div>
    </div>
  )
}

/**
 * Main Component Showcase Page
 */
function ComponentShowcase() {
  // State for interactive examples
  const [datePickerValue, setDatePickerValue] = useState<Date | undefined>()
  const [datePickerError, setDatePickerError] = useState(false)
  const [singleDate, setSingleDate] = useState<Date | undefined>()
  const [multipleDates, setMultipleDates] = useState<Date[] | undefined>()
  const [dateRange, setDateRange] = useState<
    { from: Date | undefined; to?: Date | undefined } | undefined
  >()
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})

  const toggleLoading = (key: string) => {
    setLoadingStates((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const [showAlert, setShowAlert] = useState<Record<string, boolean>>({
    default: true,
    destructive: true,
    success: true,
    warning: true,
    info: true,
  })

  return (
    <Container maxWidth="7xl" className="py-8">
      <div className="space-y-12">
        {/* Header */}
        <header className="space-y-4">
          <Text variant="h1">Component Showcase</Text>
          <Text variant="body1" className="text-muted-foreground max-w-3xl">
            Development reference for shadcn/ui components created during the MUI to
            Tailwind migration. This page demonstrates all components with interactive
            examples and code snippets.
          </Text>
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Development Tool</AlertTitle>
            <AlertDescription>
              This page is for development reference only and will be removed or hidden
              after migration completion.
            </AlertDescription>
          </Alert>
        </header>

        {/* Table of Contents */}
        <nav
          className="sticky top-16 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border rounded-lg p-4"
          aria-label="Component navigation"
        >
          <Text variant="h3" className="mb-3">
            Quick Navigation
          </Text>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {[
              { id: 'skeleton', label: 'Skeleton' },
              { id: 'alert', label: 'Alert' },
              { id: 'calendar', label: 'Calendar' },
              { id: 'datepicker', label: 'DatePicker' },
              { id: 'container', label: 'Container' },
              { id: 'stack', label: 'Stack' },
              { id: 'text', label: 'Text' },
              { id: 'loading-button', label: 'LoadingButton' },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="text-primary hover:underline focus:underline focus:outline-none"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Skeleton Component */}
        <Section
          id="skeleton"
          title="Skeleton"
          description="Loading placeholder component with multiple variants and pulse animation."
        >
          <Example title="Default (Rectangular) Variant">
            <Stack spacing={3}>
              <Skeleton className="w-full h-12" />
              <Skeleton className="w-3/4 h-12" />
              <Skeleton className="w-1/2 h-12" />
            </Stack>
          </Example>

          <CodeBlock
            code={`<Skeleton className="w-full h-12" />
<Skeleton className="w-3/4 h-12" />
<Skeleton className="w-1/2 h-12" />`}
          />

          <Example title="Circular Variant (Avatar Placeholder)">
            <Stack direction="row" spacing={3} align="center">
              <Skeleton variant="circular" className="w-12 h-12" />
              <Skeleton variant="circular" className="w-16 h-16" />
              <Skeleton variant="circular" className="w-20 h-20" />
            </Stack>
          </Example>

          <CodeBlock
            code={`<Skeleton variant="circular" className="w-12 h-12" />
<Skeleton variant="circular" className="w-16 h-16" />
<Skeleton variant="circular" className="w-20 h-20" />`}
          />

          <Example title="Text Variant">
            <Stack spacing={2}>
              <Skeleton variant="text" className="w-full" />
              <Skeleton variant="text" className="w-5/6" />
              <Skeleton variant="text" className="w-3/4" />
              <Skeleton variant="text" className="w-1/2" />
            </Stack>
          </Example>

          <CodeBlock
            code={`<Skeleton variant="text" className="w-full" />
<Skeleton variant="text" className="w-5/6" />
<Skeleton variant="text" className="w-3/4" />
<Skeleton variant="text" className="w-1/2" />`}
          />

          <Example title="Combined Example (Loading Card)">
            <div className="flex gap-4">
              <Skeleton variant="circular" className="w-12 h-12 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton variant="text" className="w-1/3" />
                <Skeleton variant="text" className="w-full" />
                <Skeleton variant="text" className="w-2/3" />
              </div>
            </div>
          </Example>

          <CodeBlock
            code={`<div className="flex gap-4">
  <Skeleton variant="circular" className="w-12 h-12 flex-shrink-0" />
  <div className="flex-1 space-y-2">
    <Skeleton variant="text" className="w-1/3" />
    <Skeleton variant="text" className="w-full" />
    <Skeleton variant="text" className="w-2/3" />
  </div>
</div>`}
          />
        </Section>

        {/* Alert Component */}
        <Section
          id="alert"
          title="Alert"
          description="Display important messages with different severity levels and optional dismiss functionality."
        >
          <Example title="Default Variant">
            {showAlert.default && (
              <Alert onClose={() => setShowAlert({ ...showAlert, default: false })}>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  This is a default alert with dismissible functionality.
                </AlertDescription>
              </Alert>
            )}
          </Example>

          <CodeBlock
            code={`<Alert>
  <AlertDescription>
    This is a default alert message.
  </AlertDescription>
</Alert>`}
          />

          <Example title="Destructive Variant">
            {showAlert.destructive && (
              <Alert
                variant="destructive"
                onClose={() => setShowAlert({ ...showAlert, destructive: false })}
              >
                <XCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  Your session has expired. Please log in again.
                </AlertDescription>
              </Alert>
            )}
          </Example>

          <CodeBlock
            code={`<Alert variant="destructive">
  <AlertTitle>Error</AlertTitle>
  <AlertDescription>
    Your session has expired. Please log in again.
  </AlertDescription>
</Alert>`}
          />

          <Example title="Success Variant">
            {showAlert.success && (
              <Alert
                variant="success"
                onClose={() => setShowAlert({ ...showAlert, success: false })}
              >
                <CheckCircle className="h-4 w-4" />
                <AlertTitle>Success!</AlertTitle>
                <AlertDescription>
                  Your changes have been saved successfully.
                </AlertDescription>
              </Alert>
            )}
          </Example>

          <CodeBlock
            code={`<Alert variant="success">
  <AlertTitle>Success!</AlertTitle>
  <AlertDescription>
    Your changes have been saved successfully.
  </AlertDescription>
</Alert>`}
          />

          <Example title="Warning Variant">
            {showAlert.warning && (
              <Alert
                variant="warning"
                onClose={() => setShowAlert({ ...showAlert, warning: false })}
              >
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                  Please review your changes before proceeding.
                </AlertDescription>
              </Alert>
            )}
          </Example>

          <CodeBlock
            code={`<Alert variant="warning">
  <AlertTitle>Warning</AlertTitle>
  <AlertDescription>
    Please review your changes before proceeding.
  </AlertDescription>
</Alert>`}
          />

          <Example title="Info Variant">
            {showAlert.info && (
              <Alert
                variant="info"
                onClose={() => setShowAlert({ ...showAlert, info: false })}
              >
                <Info className="h-4 w-4" />
                <AlertTitle>Information</AlertTitle>
                <AlertDescription>
                  New features are available. Check out the changelog for details.
                </AlertDescription>
              </Alert>
            )}
          </Example>

          <CodeBlock
            code={`<Alert variant="info">
  <AlertTitle>Information</AlertTitle>
  <AlertDescription>
    New features are available. Check out the changelog for details.
  </AlertDescription>
</Alert>`}
          />

          <Example title="Without Icon">
            <Alert icon={null}>
              <AlertDescription>
                This alert does not display an icon.
              </AlertDescription>
            </Alert>
          </Example>

          <CodeBlock
            code={`<Alert icon={null}>
  <AlertDescription>
    This alert does not display an icon.
  </AlertDescription>
</Alert>`}
          />
        </Section>

        {/* Calendar Component */}
        <Section
          id="calendar"
          title="Calendar"
          description="Fully accessible date picker with single, multiple, and range selection modes."
        >
          <Example title="Single Selection Mode">
            <div className="flex flex-col items-center">
              <Calendar mode="single" selected={singleDate} onSelect={setSingleDate} />
              {singleDate && (
                <Text variant="body2" className="mt-4">
                  Selected: {singleDate.toLocaleDateString()}
                </Text>
              )}
            </div>
          </Example>

          <CodeBlock
            code={`const [date, setDate] = useState<Date | undefined>()

<Calendar
  mode="single"
  selected={date}
  onSelect={setDate}
/>`}
          />

          <Example title="Multiple Selection Mode">
            <div className="flex flex-col items-center">
              <Calendar
                mode="multiple"
                selected={multipleDates}
                onSelect={setMultipleDates}
              />
              {multipleDates && multipleDates.length > 0 && (
                <Text variant="body2" className="mt-4">
                  Selected {multipleDates.length} date(s)
                </Text>
              )}
            </div>
          </Example>

          <CodeBlock
            code={`const [dates, setDates] = useState<Date[] | undefined>()

<Calendar
  mode="multiple"
  selected={dates}
  onSelect={setDates}
/>`}
          />

          <Example title="Range Selection Mode">
            <div className="flex flex-col items-center">
              <Calendar mode="range" selected={dateRange} onSelect={setDateRange} />
              {dateRange?.from && (
                <Text variant="body2" className="mt-4">
                  From: {dateRange.from.toLocaleDateString()}
                  {dateRange.to && ` - To: ${dateRange.to.toLocaleDateString()}`}
                </Text>
              )}
            </div>
          </Example>

          <CodeBlock
            code={`const [range, setRange] = useState<{ from: Date; to?: Date } | undefined>()

<Calendar
  mode="range"
  selected={range}
  onSelect={setRange}
/>`}
          />
        </Section>

        {/* DatePicker Component */}
        <Section
          id="datepicker"
          title="DatePicker"
          description="Form-compatible date picker with error states and accessibility features."
        >
          <Example title="Basic DatePicker">
            <div className="max-w-sm">
              <DatePicker
                value={datePickerValue}
                onChange={setDatePickerValue}
                placeholder="Select a date"
              />
              {datePickerValue && (
                <Text variant="body2" className="mt-2 text-muted-foreground">
                  Selected: {datePickerValue.toLocaleDateString()}
                </Text>
              )}
            </div>
          </Example>

          <CodeBlock
            code={`const [date, setDate] = useState<Date | undefined>()

<DatePicker
  value={date}
  onChange={setDate}
  placeholder="Select a date"
/>`}
          />

          <Example title="With Error State">
            <div className="max-w-sm space-y-2">
              <DatePicker
                value={datePickerValue}
                onChange={(date) => {
                  setDatePickerValue(date)
                  setDatePickerError(!date)
                }}
                error={datePickerError}
                placeholder="Required date field"
                ariaDescribedBy="date-error"
              />
              {datePickerError && (
                <Text
                  variant="body2"
                  className="text-red-500"
                  id="date-error"
                  role="alert"
                >
                  Date is required
                </Text>
              )}
            </div>
          </Example>

          <CodeBlock
            code={`const [date, setDate] = useState<Date | undefined>()
const [error, setError] = useState(false)

<DatePicker
  value={date}
  onChange={(date) => {
    setDate(date)
    setError(!date)
  }}
  error={error}
  ariaDescribedBy="date-error"
/>
{error && (
  <span id="date-error" className="text-red-500">
    Date is required
  </span>
)}`}
          />

          <Example title="With Date Constraints">
            <div className="max-w-sm">
              <DatePicker
                value={datePickerValue}
                onChange={setDatePickerValue}
                placeholder="Future dates only"
                fromDate={new Date()}
              />
            </div>
          </Example>

          <CodeBlock
            code={`<DatePicker
  value={date}
  onChange={setDate}
  fromDate={new Date()} // Only future dates
  toDate={addMonths(new Date(), 6)} // Up to 6 months
/>`}
          />
        </Section>

        {/* Container Component */}
        <Section
          id="container"
          title="Container"
          description="Responsive layout container with configurable max-width and padding."
        >
          <Example title="Different Max-Width Options">
            <Stack spacing={4}>
              <div className="bg-muted/50">
                <Container maxWidth="sm">
                  <Text variant="body2" className="text-center">
                    max-width: sm
                  </Text>
                </Container>
              </div>
              <div className="bg-muted/50">
                <Container maxWidth="md">
                  <Text variant="body2" className="text-center">
                    max-width: md
                  </Text>
                </Container>
              </div>
              <div className="bg-muted/50">
                <Container maxWidth="lg">
                  <Text variant="body2" className="text-center">
                    max-width: lg
                  </Text>
                </Container>
              </div>
              <div className="bg-muted/50">
                <Container maxWidth="xl">
                  <Text variant="body2" className="text-center">
                    max-width: xl
                  </Text>
                </Container>
              </div>
            </Stack>
          </Example>

          <CodeBlock
            code={`<Container maxWidth="sm">Content</Container>
<Container maxWidth="md">Content</Container>
<Container maxWidth="lg">Content</Container>
<Container maxWidth="xl">Content</Container>
<Container maxWidth="7xl">Content (default)</Container>`}
          />
        </Section>

        {/* Stack Component */}
        <Section
          id="stack"
          title="Stack"
          description="Flexible box layout component with configurable direction, spacing, and alignment."
        >
          <Example title="Column Direction with Different Spacing">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4">
                <Text variant="caption" className="mb-2 block">
                  spacing=2
                </Text>
                <Stack spacing={2}>
                  <div className="bg-primary/20 p-2 rounded">Item 1</div>
                  <div className="bg-primary/20 p-2 rounded">Item 2</div>
                  <div className="bg-primary/20 p-2 rounded">Item 3</div>
                </Stack>
              </div>
              <div className="border rounded-lg p-4">
                <Text variant="caption" className="mb-2 block">
                  spacing=4
                </Text>
                <Stack spacing={4}>
                  <div className="bg-primary/20 p-2 rounded">Item 1</div>
                  <div className="bg-primary/20 p-2 rounded">Item 2</div>
                  <div className="bg-primary/20 p-2 rounded">Item 3</div>
                </Stack>
              </div>
              <div className="border rounded-lg p-4">
                <Text variant="caption" className="mb-2 block">
                  spacing=8
                </Text>
                <Stack spacing={8}>
                  <div className="bg-primary/20 p-2 rounded">Item 1</div>
                  <div className="bg-primary/20 p-2 rounded">Item 2</div>
                  <div className="bg-primary/20 p-2 rounded">Item 3</div>
                </Stack>
              </div>
            </div>
          </Example>

          <CodeBlock
            code={`<Stack spacing={2}>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</Stack>`}
          />

          <Example title="Row Direction with Alignment">
            <Stack spacing={4}>
              <div>
                <Text variant="caption" className="mb-2 block">
                  justify="start"
                </Text>
                <Stack direction="row" spacing={2} justify="start" className="border p-4 rounded-lg">
                  <div className="bg-primary/20 p-2 rounded">Item 1</div>
                  <div className="bg-primary/20 p-2 rounded">Item 2</div>
                  <div className="bg-primary/20 p-2 rounded">Item 3</div>
                </Stack>
              </div>
              <div>
                <Text variant="caption" className="mb-2 block">
                  justify="center"
                </Text>
                <Stack direction="row" spacing={2} justify="center" className="border p-4 rounded-lg">
                  <div className="bg-primary/20 p-2 rounded">Item 1</div>
                  <div className="bg-primary/20 p-2 rounded">Item 2</div>
                  <div className="bg-primary/20 p-2 rounded">Item 3</div>
                </Stack>
              </div>
              <div>
                <Text variant="caption" className="mb-2 block">
                  justify="between"
                </Text>
                <Stack direction="row" spacing={2} justify="between" className="border p-4 rounded-lg">
                  <div className="bg-primary/20 p-2 rounded">Item 1</div>
                  <div className="bg-primary/20 p-2 rounded">Item 2</div>
                  <div className="bg-primary/20 p-2 rounded">Item 3</div>
                </Stack>
              </div>
            </Stack>
          </Example>

          <CodeBlock
            code={`<Stack direction="row" spacing={2} justify="between" align="center">
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</Stack>`}
          />
        </Section>

        {/* Text Component */}
        <Section
          id="text"
          title="Text"
          description="Typography component with semantic HTML mapping and consistent styling."
        >
          <Example title="All Text Variants">
            <Stack spacing={4}>
              <Text variant="h1">Heading 1 - Page Title</Text>
              <Text variant="h2">Heading 2 - Section Title</Text>
              <Text variant="h3">Heading 3 - Subsection Title</Text>
              <Text variant="h4">Heading 4 - Component Title</Text>
              <Text variant="h5">Heading 5 - Small Heading</Text>
              <Text variant="h6">Heading 6 - Smallest Heading</Text>
              <Text variant="body1">
                Body 1 - Regular paragraph text with base font size for main content.
              </Text>
              <Text variant="body2">
                Body 2 - Smaller text for secondary content and descriptions.
              </Text>
              <Text variant="caption">
                Caption - Small text for labels, captions, and metadata.
              </Text>
            </Stack>
          </Example>

          <CodeBlock
            code={`<Text variant="h1">Heading 1</Text>
<Text variant="h2">Heading 2</Text>
<Text variant="h3">Heading 3</Text>
<Text variant="body1">Regular paragraph text</Text>
<Text variant="body2">Smaller secondary text</Text>
<Text variant="caption">Caption text</Text>`}
          />

          <Example title="Custom Semantic Element">
            <Stack spacing={2}>
              <Text variant="h1" as="h2">
                H1 styling rendered as H2 element
              </Text>
              <Text variant="body1" as="div">
                Body text rendered as div element
              </Text>
            </Stack>
          </Example>

          <CodeBlock
            code={`// Render h1 styling as h2 element (for SEO/accessibility)
<Text variant="h1" as="h2">Section Title</Text>

// Render body text as div
<Text variant="body1" as="div">Content</Text>`}
          />
        </Section>

        {/* LoadingButton Component */}
        <Section
          id="loading-button"
          title="LoadingButton"
          description="Button component with loading state support and all button variants."
        >
          <Example title="All Button Variants">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Primary
                </Text>
                <LoadingButton
                  variant="primary"
                  loading={loadingStates.primary}
                  onClick={() => toggleLoading('primary')}
                  className="w-full"
                >
                  {loadingStates.primary ? 'Loading...' : 'Click Me'}
                </LoadingButton>
              </div>
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Secondary
                </Text>
                <LoadingButton
                  variant="secondary"
                  loading={loadingStates.secondary}
                  onClick={() => toggleLoading('secondary')}
                  className="w-full"
                >
                  {loadingStates.secondary ? 'Loading...' : 'Click Me'}
                </LoadingButton>
              </div>
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Outline
                </Text>
                <LoadingButton
                  variant="outline"
                  loading={loadingStates.outline}
                  onClick={() => toggleLoading('outline')}
                  className="w-full"
                >
                  {loadingStates.outline ? 'Loading...' : 'Click Me'}
                </LoadingButton>
              </div>
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Ghost
                </Text>
                <LoadingButton
                  variant="ghost"
                  loading={loadingStates.ghost}
                  onClick={() => toggleLoading('ghost')}
                  className="w-full"
                >
                  {loadingStates.ghost ? 'Loading...' : 'Click Me'}
                </LoadingButton>
              </div>
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Danger
                </Text>
                <LoadingButton
                  variant="danger"
                  loading={loadingStates.danger}
                  onClick={() => toggleLoading('danger')}
                  className="w-full"
                >
                  {loadingStates.danger ? 'Deleting...' : 'Delete'}
                </LoadingButton>
              </div>
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Default
                </Text>
                <LoadingButton
                  variant="default"
                  loading={loadingStates.default}
                  onClick={() => toggleLoading('default')}
                  className="w-full"
                >
                  {loadingStates.default ? 'Loading...' : 'Click Me'}
                </LoadingButton>
              </div>
              <div className="space-y-2">
                <Text variant="caption" className="block">
                  Link
                </Text>
                <LoadingButton
                  variant="link"
                  loading={loadingStates.link}
                  onClick={() => toggleLoading('link')}
                  className="w-full"
                >
                  {loadingStates.link ? 'Loading...' : 'Link Style'}
                </LoadingButton>
              </div>
            </div>
          </Example>

          <CodeBlock
            code={`const [loading, setLoading] = useState(false)

const handleSubmit = async () => {
  setLoading(true)
  try {
    await saveData()
  } finally {
    setLoading(false)
  }
}

<LoadingButton
  loading={loading}
  onClick={handleSubmit}
  variant="primary"
>
  Save Changes
</LoadingButton>`}
          />

          <Example title="Button Sizes">
            <Stack direction="row" spacing={4} align="center" wrap>
              <LoadingButton
                size="sm"
                loading={loadingStates.small}
                onClick={() => toggleLoading('small')}
              >
                Small
              </LoadingButton>
              <LoadingButton
                size="md"
                loading={loadingStates.medium}
                onClick={() => toggleLoading('medium')}
              >
                Medium (Default)
              </LoadingButton>
              <LoadingButton
                size="lg"
                loading={loadingStates.large}
                onClick={() => toggleLoading('large')}
              >
                Large
              </LoadingButton>
            </Stack>
          </Example>

          <CodeBlock
            code={`<LoadingButton size="sm" loading={loading}>Small</LoadingButton>
<LoadingButton size="md" loading={loading}>Medium</LoadingButton>
<LoadingButton size="lg" loading={loading}>Large</LoadingButton>`}
          />
        </Section>

        {/* Footer */}
        <footer className="border-t pt-8">
          <Stack spacing={4}>
            <Alert variant="info">
              <Info className="h-4 w-4" />
              <AlertTitle>Migration Reference</AlertTitle>
              <AlertDescription>
                This showcase page provides visual examples and code references for all
                shadcn/ui components created during Epic 1: Foundation & Tooling Setup.
                Use these examples as reference when migrating MUI components to Tailwind
                CSS in Epics 2-6.
              </AlertDescription>
            </Alert>
            <Text variant="body2" className="text-muted-foreground text-center">
              Component Showcase - MUI to Tailwind Migration (MONU-TAILWIND Epic 1)
            </Text>
          </Stack>
        </footer>
      </div>
    </Container>
  )
}
